import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

@Directive({
  standalone: true,
  selector: '[appDragScroll]',
})
export class DragScrollDirective implements OnInit, OnChanges, OnDestroy {
  /**
   * @description
   * Enable drag scroll functionality
   * @type {boolean}
   * @default false
   */
  @Input('appDragScroll')
  public isDragScrollEnabled: boolean = false;

  // Sensitivity of drag scroll (higher = more sensitive)
  private readonly _sensitivity: number = 1.2;
  private _isDragging: boolean = false;
  private _startX: number = 0;
  private _scrollLeft: number = 0;
  private readonly _element: HTMLElement;

  public constructor(private readonly _elementRef: ElementRef) {
    this._element = this._elementRef.nativeElement;
  }

  public ngOnInit(): void {
    this._initDragScroll();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDragScrollEnabled']) {
      this._updateCursor();
    }
  }

  public ngOnDestroy(): void {
    this._destroyDragScroll();
  }

  @HostListener('mousedown', ['$event'])
  public onMouseDown(event: MouseEvent): void {
    if (!this.isDragScrollEnabled || event.button !== 0) {
      return;
    }

    this._isDragging = true;
    this._startX = event.pageX - this._element.offsetLeft;
    this._scrollLeft = this._element.scrollLeft;

    this._element.style.cursor = 'grabbing';
    this._element.style.userSelect = 'none';

    event.preventDefault();
  }

  @HostListener('mousemove', ['$event'])
  public onMouseMove(event: MouseEvent): void {
    if (!this._isDragging || !this.isDragScrollEnabled) {
      return;
    }

    const currentX = event.pageX - this._element.offsetLeft;
    const deltaX = (currentX - this._startX) * this._sensitivity;

    this._element.scrollLeft = this._scrollLeft - deltaX;

    event.preventDefault();
  }

  @HostListener('mouseup', ['$event'])
  public onMouseUp(event: MouseEvent): void {
    if (!this.isDragScrollEnabled) {
      return;
    }

    this._isDragging = false;
    this._element.style.cursor = 'grab';
    this._element.style.userSelect = 'auto';

    event.preventDefault();
  }

  @HostListener('mouseleave', ['$event'])
  public onMouseLeave(_event: MouseEvent): void {
    if (!this.isDragScrollEnabled) {
      return;
    }

    this._isDragging = false;
    this._element.style.cursor = 'grab';
    this._element.style.userSelect = 'auto';
  }

  @HostListener('dragstart', ['$event'])
  public onDragStart(event: DragEvent): void {
    if (this.isDragScrollEnabled) {
      event.preventDefault();
    }
  }

  private _initDragScroll(): void {
    this._updateCursor();
  }

  private _destroyDragScroll(): void {
    this._element.style.cursor = '';
    this._element.style.userSelect = '';
  }

  private _updateCursor(): void {
    if (this.isDragScrollEnabled) {
      this._element.style.cursor = 'grab';
    } else {
      this._element.style.cursor = '';
    }
  }
}
